import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import TelegramIcon from "@mui/icons-material/Telegram"
import {
    DialogTitle,
    DialogContent,
    Stack,
    DialogActions,
    DialogContentText,
    Box,
    useTheme,
    Collapse,
    AlertTitle,
} from "@mui/material"
import { Dispatch, ReactElement, SetStateAction } from "react"
import { useForm } from "react-hook-form"

import { Datasets } from "../../../constants/enums"
import useRaiseAlert from "../../../services/middleware/hooks/deviceRegisters/useRaiseAlert"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import AppDialog from "../../dialog/appDialog"
import ValidatedTextField from "../../inputs/validated/validatedTextField"
import { AppCardHeader } from "../../surfaces/appCard"
import { COMMENT_REG, MAX_COMMENT_LENGTH } from "../dnoResponse/dnoResponseForm"

interface IAlertResponseForm {
    deviceSystemRef: string
    dataset?: Datasets
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    mutate: () => void
}

/**
 * Dialog where admins can raise alerts on TTR devices.
 * This is an uncontrolled form, registered with react-hook-form.
 * If a component is mounted, it is automatically registered with react-hook-form.
 */
const AlertResponseForm = ({
    deviceSystemRef,
    dataset,
    isOpen,
    setIsOpen,
    mutate,
}: IAlertResponseForm): ReactElement => {
    const theme = useTheme()
    const { control, handleSubmit, watch } = useForm({
        shouldUnregister: true,
    })
    const { error, isSending, postResponse } = useRaiseAlert(setIsOpen)

    const submitResponse = (): void => {
        const description = watch(COMMENT_REG)
        postResponse(deviceSystemRef, description, dataset).then(mutate)
    }

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    sx={{ p: 0 }}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={
                        <>
                            Raise alert for{" "}
                            <Box
                                color={theme.palette.secondary.main}
                                display="inline"
                            >
                                {deviceSystemRef}
                            </Box>{" "}
                        </>
                    }
                    subheader="An alert can be used to flag an issue with this device"
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={GRID_SPACING} paddingTop="8px">
                    <DialogContentText>
                        Raising an alert will trigger an email to be sent to all
                        registered DNO users of Connect Direct, along with the
                        manufacture of this device. Your name will be included
                        in these communications.
                    </DialogContentText>
                    <Collapse in={!!error} mountOnEnter>
                        <AppAlert severity={error?.level}>
                            <AlertTitle>{error?.message}</AlertTitle>
                            {error?.displayDetail}
                        </AppAlert>
                    </Collapse>
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={COMMENT_REG}
                        label="Description"
                        placeholder="Describe the alert"
                        multiline
                        customRules={{
                            maxLength: {
                                value: MAX_COMMENT_LENGTH,
                                message: `Max permitted characters: ${MAX_COMMENT_LENGTH}`,
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={() => setIsOpen(false)}
                >
                    Back
                </AppButton>
                <AppLoadingButton
                    color="success"
                    variant="contained"
                    endIcon={<TelegramIcon />}
                    loading={isSending}
                    onClick={handleSubmit(submitResponse)}
                >
                    Submit
                </AppLoadingButton>
            </DialogActions>
        </AppDialog>
    )
}

AlertResponseForm.defaultProps = {
    dataset: undefined,
}

export default AlertResponseForm
